<template>
  <div>
    <v-row>
      <v-col cols="12">
        <h4>By Event Type</h4>
        <div>
          <IncidentsCategoryToggles
            :selectedPolitical="politicalEnabled"
            :selectedMaritime="maritimeEnabled"
            :selectedCategories="selectedCategories"
            @change-categories="handleCategoriesChange"
            @change-political="handleChangePoliticalCategory"
            @change-maritime="handleChangeMaritimeCategory"
          ></IncidentsCategoryToggles>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="politicalEnabled">
      <v-col cols="12" disabled="true">
        <h4 :class="{ 'hyp-disabled-text': disabledFilter }">By Sector</h4>
        <div>
          <IncidentsSectorSelector
            :disabled="disabledFilter"
            :selected="selectedSectors"
            @change="handleSectorChange"
          ></IncidentsSectorSelector>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="politicalEnabled">
      <v-col cols="12">
        <h4 :class="{ 'hyp-disabled-text': disabledFilter }">By Asset</h4>
        <div>
          <IncidentsAssetSelector
            :disabled="disabledFilter"
            :selected="selectedAssets"
            @change="handleAssetChange"
          ></IncidentsAssetSelector>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <h4 :class="{ 'hyp-disabled-text': disabledFilter }">By Date Range</h4>
        <div>
          <IncidentsTimeSlider
            :disabled="disabledFilter"
            @change="handleUpdateTimeRange"
            :selected="selectedTimeRange"
          ></IncidentsTimeSlider>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="politicalEnabled">
      <v-col cols="12">
        <h4 :class="{ 'hyp-disabled-text': disabledFilter }">By Severity</h4>
        <div>
          <IncidentsSeveritySlider
            :disabled="disabledFilter"
            :selected="selectedSeverityRange"
            @change="handleSeverityChange"
          ></IncidentsSeveritySlider>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { MODULE_PREFIX_MAP, MODULE_PREFIX_USER } from "@/store";

import POLITICAL_CATEGORIES from "@/common/constants/controlRisks/IncidentsPoliticalCategories";
import MARITIME_CATEGORIES from "@/common/constants/controlRisks/IncidentsMaritimeCategories";
import SECTORS from "@/common/constants/controlRisks/sectors";
import ASSETS from "@/common/constants/controlRisks/assets";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

const DEFAULT_TIME_RANGE = [-30, 0];
const DEFAULT_SEVERITY_RANGE = [0, 10];

const EMITTED_ACTIONS = {
  CHANGE_ASSETS: "change-assets",
  CHANGE_CATEGORIES: "change-categories",
  CHANGE_POLITICAL_ENABLED: "change-political-enabled",
  CHANGE_MARITIME_ENABLED: "change-maritime-enabled",
  CHANGE_SECTORS: "change-sectors",
  CHANGE_SEVERITIES: "change-severities",
  CHANGE_DATES: "change-dates",
  CHANGE_HAS_FILTERS_SET: "change-has-filters-set",
  CHANGE_SHOULD_PULL_DATA: "change-should-pull-data",
  RESET: "reset",
};

export default {
  name: "IncidentsContentFilters",
  components: {
    IncidentsCategoryToggles: () =>
      import("@/components/controlRisks/incidents/IncidentsCategoryToggles"),
    IncidentsSectorSelector: () =>
      import("@/components/controlRisks/incidents/IncidentsSectorSelector"),
    IncidentsAssetSelector: () =>
      import("@/components/controlRisks/incidents/IncidentsAssetSelector"),
    IncidentsSeveritySlider: () =>
      import("@/components/search/SearchSeveritySlider.vue"),
    IncidentsTimeSlider: () =>
      import("@/components/controlRisks/incidents/IncidentsTimeSlider"),
  },
  props: {
    resetTimestamp: {
      type: String,
      default: new Date().toISOString(),
    },
  },
  data() {
    return {
      selectedCategories: [],
      politicalEnabled: false,
      maritimeEnabled: false,
      selectedTimeRange: DEFAULT_TIME_RANGE,
      selectedSeverityRange: DEFAULT_SEVERITY_RANGE,
      selectedSectors: [],
      selectedAssets: [],
    };
  },
  async mounted() {
    if (
      this.preferences.controlRisks &&
      this.preferences.controlRisks.incidents
    ) {
      this.initialize(this.preferences.controlRisks.incidents);
    }
  },
  computed: {
    ...mapGetters({
      //TODO should remember settings I guess
      preferences: MODULE_PREFIX_USER + "preferences",
      resetMapIcons: MODULE_PREFIX_MAP + "resetMapIcons",
    }),
    disabledFilter() {
      return !this.politicalEnabled && !this.maritimeEnabled;
    },
  },
  methods: {
    initialize(params) {
      this.selectedCategories = params.categoriesId;
      this.selectedSectors = params.sectorsId;
      this.selectedAssets = params.assetTypes;
      this.selectedTimeRange = params.timeRange;
      this.selectedSeverityRange = params.severityScores
        ? params.severityScores
        : DEFAULT_SEVERITY_RANGE;
      this.politicalEnabled = params.politicalEnabled;
      this.maritimeEnabled = params.maritimeEnabled;
    },
    handleCategoriesChange(categories) {
      const size = this.selectedCategories ? this.selectedCategories.length : 0;
      if (categories && categories.length > size) {
        let categoriesList = POLITICAL_CATEGORIES;
        let categoryName = "Security";
        if (!this.politicalEnabled) {
          categoriesList = MARITIME_CATEGORIES;
          categoryName = "Maritime";
        }
        const _categories = JSON.parse(JSON.stringify(categories));
        const _categoryId = _categories[_categories.length - 1];
        if (_categoryId) {
          const value = categoriesList.filter(
            (category) => category.Id === _categoryId
          )[0]["Name"];
          this.handlePageTrackerEvent(
            EVENT_CATEGORIES.control_risk_events_verified,
            EVENT_ACTIONS.clicked,
            categoryName + " - Category - " + value
          );
        }
      }
      this.selectedCategories = categories;
      this.$emit(EMITTED_ACTIONS.CHANGE_CATEGORIES, this.selectedCategories);
    },
    handleChangePoliticalCategory(enabled) {
      if (enabled && this.politicalEnabled != enabled) {
        this.handleSeverityChange(DEFAULT_SEVERITY_RANGE);
      }
      this.politicalEnabled = enabled;
      this.$emit(
        EMITTED_ACTIONS.CHANGE_POLITICAL_ENABLED,
        this.politicalEnabled
      );
    },
    handleChangeMaritimeCategory(enabled) {
      if (enabled) {
        this.handleSeverityChange([]);
        this.handleAssetChange([]);
        this.handleSectorChange([]);
      }
      this.maritimeEnabled = enabled;
      this.$emit(EMITTED_ACTIONS.CHANGE_MARITIME_ENABLED, this.maritimeEnabled);
    },
    handleSectorChange(sectors) {
      const size = this.selectedSectors ? this.selectedSectors.length : 0;
      if (sectors && sectors.length > size) {
        let sectorList = SECTORS;
        const _sectors = JSON.parse(JSON.stringify(sectors));
        const _sectorId = _sectors[_sectors.length - 1];
        if (_sectorId) {
          const value = sectorList.filter(
            (sector) => sector.Id === _sectorId
          )[0]["Name"];
          this.handlePageTrackerEvent(
            EVENT_CATEGORIES.control_risk_events_verified,
            EVENT_ACTIONS.clicked,
            "Security - Sector - " + value
          );
        }
      }
      this.selectedSectors = sectors;
      this.$emit(EMITTED_ACTIONS.CHANGE_SECTORS, this.selectedSectors);
    },
    handleAssetChange(assets) {
      const size = this.selectedAssets ? this.selectedAssets.length : 0;
      if (assets && assets.length > size) {
        let assetList = ASSETS;
        const _assets = JSON.parse(JSON.stringify(assets));
        const _assetId = _assets[_assets.length - 1];
        if (_assetId) {
          const value = assetList.filter((sector) => sector.Id === _assetId)[0][
            "Name"
          ];
          this.handlePageTrackerEvent(
            EVENT_CATEGORIES.control_risk_events_verified,
            EVENT_ACTIONS.clicked,
            "Security - Asset - " + value
          );
        }
      }
      this.selectedAssets = assets;
      this.$emit(EMITTED_ACTIONS.CHANGE_ASSETS, this.selectedAssets);
    },
    handleUpdateTimeRange(timeRange) {
      this.selectedTimeRange = timeRange;
      this.$emit(EMITTED_ACTIONS.CHANGE_DATES, this.selectedTimeRange);
    },
    handleSeverityChange(severityRange) {
      const currentRange = JSON.parse(
        JSON.stringify(this.selectedSeverityRange)
      );
      const range = JSON.parse(JSON.stringify(severityRange));
      if (currentRange[0] != range[0] || currentRange[1] != range[1]) {
        this.handlePageTrackerEvent(
          EVENT_CATEGORIES.control_risk_events_verified,
          EVENT_ACTIONS.clicked,
          "Security - Severity - " + range[0] + " to " + range[1]
        );
      }
      this.selectedSeverityRange = severityRange;
      this.$emit(EMITTED_ACTIONS.CHANGE_SEVERITIES, this.selectedSeverityRange);
    },
    reset() {
      this.selectedCategories = [];
      this.selectedSectors = [];
      this.selectedAssets = [];
      this.selectedTimeRange = DEFAULT_TIME_RANGE;
      this.selectedSeverityRange = DEFAULT_SEVERITY_RANGE;
    },
    async clearAllDataFromMap() {
      this.reset();
      this.politicalEnabled = false;
      this.maritimeEnabled = false;
      this.$emit(EMITTED_ACTIONS.CHANGE_POLITICAL_ENABLED, false);
      this.$emit(EMITTED_ACTIONS.CHANGE_MARITIME_ENABLED, false);
      this.$emit(EMITTED_ACTIONS.RESET, new Date().toISOString());
    },
  },
  watch: {
    resetTimestamp: {
      handler() {
        this.reset();
      },
    },
    resetMapIcons: {
      handler(newValue, oldValue) {
        if (oldValue !== newValue) {
          this.clearAllDataFromMap();
        }
      },
    },
  },
};
</script>

<style scoped>
.hyp-disabled-text {
  color: gray;
}
</style>
